import { Suspense, useCallback, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { usePageConfig } from '@hooks/usePageConfig'
import { useTableHighlight } from '@hooks/useTableHighlight'
import ErrorBoundary from '@components/ErrorBoundary/ErrorBoundary'
import ErrorMessage from '@components/ErrorBoundary/ErrorMessage'
import QueryList from './QueryList/QueryList'
import ExternalHeaderContent from '@components/ExternalHeaderContent/ExternalHeaderContent'
import SortSelector from '@components/SortSelector/SortSelector'
import ItemListSkeleton from '@components/skeletons/ItemListSkeleton/ItemListSkeleton'
import { useSearchParams } from 'react-router'
import Tabs from '@components/Tabs/Tabs'

import './Explore.css'
import DashboardList from './DashboardList/DashboardList'

export default function Explore() {
  const intl = useIntl()
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchQuery, setSearchQuery] = useState('')
  const gridRef = useRef<HTMLDivElement>(null)

  const currentScope: 'dashboards' | 'queries' =
    searchParams.get('scope') === 'dashboards' ? 'dashboards' : 'queries'

  useTableHighlight(gridRef, searchQuery)

  const headerContent = useMemo(() => {
    const crumbs = [
      {
        label: intl.formatMessage({ id: 'nav.explore' }),
        path: '/explore'
      }
    ]
    return <ExternalHeaderContent hasBack crumbs={crumbs} />
  }, [intl])

  usePageConfig('explore', headerContent)

  const tabs = useMemo(
    () => [
      { id: 'queries', label: intl.formatMessage({ id: 'tab.queries' }) },
      {
        id: 'dashboards',
        label: intl.formatMessage({ id: 'tab.dashboards' })
      }
    ],
    [intl]
  )

  const handleTabChange = useCallback(
    (id: string) => {
      setSearchParams((prev) => {
        const sort = prev.get('sort')
        const next = new URLSearchParams()
        if (id === 'dashboards') next.set('scope', 'dashboards')
        if (sort) next.set('sort', sort)
        return next
      })
    },
    [setSearchParams]
  )

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value)
    },
    []
  )

  return (
    <section id="explore-content" className="main-content">
      <h1>
        <FormattedMessage id="nav.explore" />
      </h1>
      <header>
        <div className="container">
          <Tabs
            tabs={tabs}
            activeTab={currentScope}
            onChange={handleTabChange}
          />
          <div className="params-wrapper">
            <search className="search-highlight">
              <input
                type="search"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder={intl.formatMessage({
                  id: 'search-data.placeholder'
                })}
              />
            </search>
            <SortSelector />
          </div>
        </div>
      </header>
      <div id="queries-grid" ref={gridRef}>
        <div className="container">
          <ErrorBoundary errorComponent={ErrorMessage}>
            <Suspense fallback={<ItemListSkeleton />}>
              {currentScope === 'dashboards' ? (
                <DashboardList scope="public" />
              ) : (
                <QueryList scope="public" />
              )}
            </Suspense>
          </ErrorBoundary>
        </div>
      </div>
    </section>
  )
}
